import { useTranslation } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { getCurrentOrgId, getPrincipalObjectExportLimit, isPrincipalImportExportEnabled, isScriptabilityEnabled, isTmlObjectIdEnabled, } from '@thoughtspot/session-service';
import { getClientId } from '@thoughtspot/system-config-service';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { useAddUsersToOrgMutation, useAssignUsersToGroupsFromUserMutation, useDeleteUserMutation, useGenerateOtpMutation, useReInviteMemberMutation, useResetMfaConifgMutation, useUpdateUserMutation, } from "../../../../services/user-group/user-group-service";
import { BasePageContext } from "../../../../contexts/basepage-context";
import { getDefaultSamlHeaders } from '../../../saml/constant';
import { isFreeTrialOrTeamOrDeveloperUser } from '../../../user-management/users/util';
import { captureFailureEvent, isExportObjectLimitReached, MODE, } from '../../../user-management/util';
import { redirectToImport, useAdminUserTracking } from '../shared';
import styles from './components/users.module.scss';
import { UsersList } from './components/UsersList';
import { UsersModals } from './components/UsersModals';
import { ActiveModal, DEFAULT_BATCH_SIZE, ListActionTypeV2, TableColumnId, UserStateTransition, V2TranslationKeys, } from './constants';
import { useExportUsers } from './hooks/useExportUsers';
import { useOrgNameMap } from './hooks/useOrgNameMap';
import { useUsersList } from './hooks/useUsersList';
import { cleanUserPayload, getBulkActions, getColumnDefinitionsV2, handleGraphQLError, } from './utils';
const toStringArray = (v) => {
    if (v == null)
        return [];
    return Array.isArray(v) ? v : [v];
};
const UsersContainer = ({ currentOrgId: propOrgId, }) => {
    const { t } = useTranslation();
    const { metadataList } = BasePageContext();
    const tracking = useAdminUserTracking();
    const currentOrgId = propOrgId ?? getCurrentOrgId();
    const isClusterAdmin = currentOrgId === -1;
    const isTmlObjectIdFlagEnabled = isTmlObjectIdEnabled();
    const isTmlEnabled = isPrincipalImportExportEnabled() && isScriptabilityEnabled();
    const [currentUser, setCurrentUser] = useState(null);
    const [activeModal, setActiveModal] = useState(ActiveModal.NONE);
    const [settingsInitialTab, setSettingsInitialTab] = useState('scim');
    const [toast, setToast] = useState({ message: null });
    const [errorMessage, setErrorMessage] = useState(null);
    const showError = useCallback((msg) => setErrorMessage(msg), []);
    const hideError = useCallback(() => setErrorMessage(null), []);
    const closeModal = useCallback(() => setActiveModal(ActiveModal.NONE), []);
    const clearToast = useCallback(() => setToast({ message: null }), []);
    useEffect(() => {
        if (sessionStorage.getItem('MfaAcceptedOpenSettings') === 'true') {
            sessionStorage.removeItem('MfaAcceptedOpenSettings');
            setSettingsInitialTab('mfaSettings');
            setActiveModal(ActiveModal.SETTINGS);
        }
    }, []);
    const { orgNameMap, orgsListLoading } = useOrgNameMap({
        skip: !isClusterAdmin,
    });
    const { rowData, gridLoading, isLoading, setIsLoading, selectedRowsIds, setSelectedRowsIds, userListInput, isLastBatch, usersListError, refreshUsersList, onChangeOffset, onSearchChange, onSortingChanged, onRowsSelected, onGridReady, onGroupFilterChange, onOrgFilterChange, onStatusFilterChange, onClearAllFilters, } = useUsersList({
        isClusterAdmin,
        currentOrgId,
        orgNameMap,
        orgsListLoading,
    });
    const orgFilterOptions = useMemo(() => Object.entries(orgNameMap).map(([id, name]) => ({
        id: String(id),
        name,
    })), [orgNameMap]);
    useEffect(() => {
        if (usersListError) {
            showError(t('admin.error.generic'));
        }
    }, [usersListError, showError, t]);
    const tabViewedFiredRef = useRef(false);
    useEffect(() => {
        if (tabViewedFiredRef.current || gridLoading)
            return;
        tabViewedFiredRef.current = true;
        const entrySource = /#\/?.*users/i.test(window.location.hash)
            ? 'deeplink'
            : 'sidebar';
        tracking.tabViewed({ entrySource, userCount: rowData.length });
    }, [gridLoading, rowData.length, tracking]);
    const { shouldExportDependencies, isGetEdocLoading, exportUsers, onExportRadioChange, } = useExportUsers({
        isClusterAdmin,
        isTmlObjectIdFlagEnabled,
        onError: showError,
        onClose: closeModal,
        clearSelection: () => setSelectedRowsIds([]),
        setIsLoading,
    });
    const [generatedOtp, setGeneratedOtp] = useState('');
    const [otpErrorMessage, setOtpErrorMessage] = useState('');
    const [showOtpLoader, setShowOtpLoader] = useState(false);
    const { deleteUser, loading: deleteUserLoading } = useDeleteUserMutation();
    const { updateUser } = useUpdateUserMutation();
    const { generateOtp } = useGenerateOtpMutation();
    const { assignUsersToGroupsFromUser, } = useAssignUsersToGroupsFromUserMutation();
    const { addUsersToOrg } = useAddUsersToOrgMutation();
    const { data: reInviteData, error: reInviteError, loading: reInviteLoading, reInviteMember, } = useReInviteMemberMutation();
    const { data: resetMfaData, error: resetMfaError, loading: resetMfaLoading, resetMfaConfig, } = useResetMfaConifgMutation();
    const onSuccess = (successMode) => {
        hideError();
        refreshUsersList();
        const messageMap = {
            [MODE.DELETE]: t(V2TranslationKeys.DELETE_SUCCESS),
            [MODE.ADD]: t(V2TranslationKeys.ADD_SUCCESS),
            [MODE.UPDATE]: t(V2TranslationKeys.UPDATE_SUCCESS),
            [MODE.ASSIGN_USERS_GROUPS]: t(V2TranslationKeys.UPDATE_SUCCESS),
            [MODE.ASSIGN_USERS_ORG]: t(V2TranslationKeys.UPDATE_SUCCESS),
            [MODE.RESEND_ACTIVATION]: t(V2TranslationKeys.ACTIVATION_MAIL_SENT),
            [MODE.RESET_MFA]: t(V2TranslationKeys.RESET_MFA_SUCCESS),
        };
        setToast({
            message: messageMap[successMode] || t(V2TranslationKeys.UPDATE_SUCCESS),
        });
    };
    const pendingReinviteIdRef = useRef(null);
    useEffect(() => {
        if (!reInviteLoading) {
            if (!_.isNil(reInviteData)) {
                onSuccess(MODE.RESEND_ACTIVATION);
                if (pendingReinviteIdRef.current) {
                    tracking.userInvited({
                        userID: pendingReinviteIdRef.current,
                        isReinvite: true,
                        isBulk: false,
                        userCount: 1,
                    });
                    pendingReinviteIdRef.current = null;
                }
                setIsLoading(false);
            }
            if (reInviteError) {
                showError(t(V2TranslationKeys.ACTIVATION_MAIL_ERROR));
                pendingReinviteIdRef.current = null;
                setIsLoading(false);
            }
        }
    }, [reInviteData, reInviteError, reInviteLoading]);
    useEffect(() => {
        if (resetMfaLoading)
            return;
        if (resetMfaError) {
            showError(t(V2TranslationKeys.RESET_MFA_ERROR));
            setIsLoading(false);
        }
        else if (!_.isNil(resetMfaData)) {
            onSuccess(MODE.RESET_MFA);
            setIsLoading(false);
        }
    }, [resetMfaData, resetMfaError, resetMfaLoading]);
    const updateUserState = async (userId, state, previousStatus = '') => {
        setIsLoading(true);
        try {
            await updateUser({
                userid: userId,
                content: {},
                password: '',
                userAccountState: state,
                isAllOrgContext: isClusterAdmin,
                triggeredbyadmin: true,
            });
            tracking.statusChanged({
                targetUserId: userId,
                previousStatus,
                newStatus: state,
            });
            if (state === UserStateTransition.REACTIVATE) {
                onSuccess(MODE.RESEND_ACTIVATION);
                tracking.userInvited({
                    userID: userId,
                    isReinvite: true,
                    isBulk: false,
                    userCount: 1,
                });
            }
            else {
                refreshUsersList();
            }
        }
        catch {
            if (state === UserStateTransition.REACTIVATE) {
                showError(t(V2TranslationKeys.ACTIVATION_MAIL_ERROR));
            }
        }
        finally {
            setIsLoading(false);
        }
    };
    const onDeleteConfirm = async () => {
        hideError();
        const fallbackIds = currentUser ? [currentUser.id] : [];
        const ids = selectedRowsIds.length ? [...selectedRowsIds] : fallbackIds;
        if (!ids.length)
            return;
        const deleteStartedAt = Date.now();
        const isBulk = ids.length > 1;
        ids.forEach(id => tracking.deleteConfirmed({
            targetUserId: id,
            confirmationMethod: 'checkbox',
        }));
        try {
            await deleteUser(ids, isClusterAdmin);
            closeModal();
            onSuccess(MODE.DELETE);
            const deletionDurationMs = Date.now() - deleteStartedAt;
            ids.forEach(id => tracking.deleteSucceeded({
                targetUserId: id,
                deletionDurationMs,
            }));
            if (isBulk) {
                tracking.bulkActionPerformed({
                    action: 'delete',
                    selectedCount: ids.length,
                    successCount: ids.length,
                    failureCount: 0,
                });
            }
            setSelectedRowsIds([]);
            const offset = userListInput.record_offset ?? 0;
            const batchSize = userListInput.record_size ?? DEFAULT_BATCH_SIZE;
            if (offset && ids.length === rowData.length) {
                onChangeOffset(offset - batchSize);
            }
        }
        catch (err) {
            const { graphQlError, errorCode, info } = handleGraphQLError(err, showError, t);
            const errorMessage = t(info.summary) ?? '';
            ids.forEach(id => tracking.deleteFailed({
                targetUserId: id,
                errorCode,
                errorMessage,
            }));
            if (isBulk) {
                tracking.bulkActionPerformed({
                    action: 'delete',
                    selectedCount: ids.length,
                    successCount: 0,
                    failureCount: ids.length,
                });
            }
            setSelectedRowsIds([]);
            closeModal();
            const failureAction = new UserAction(userActionTypeEnum.DELETE_USERS);
            captureFailureEvent(failureAction.name, graphQlError, errorMessage);
        }
    };
    const onAddToGroupsConfirm = async (payload) => {
        hideError();
        const cleaned = payload?.map((user) => cleanUserPayload(user));
        const userIds = Array.isArray(cleaned)
            ? cleaned.map((u) => u?.userId ?? u?.id).filter(Boolean)
            : [];
        try {
            await assignUsersToGroupsFromUser({ content: cleaned });
            onSuccess(MODE.ASSIGN_USERS_GROUPS);
            const groupCount = Array.isArray(cleaned)
                ? cleaned[0]?.assignedGroups?.length ?? 0
                : 0;
            userIds.forEach(id => tracking.groupAssigned({
                targetUserId: id,
                groupCount,
            }));
            tracking.bulkActionPerformed({
                action: 'assign_group',
                selectedCount: userIds.length || selectedRowsIds.length,
                successCount: userIds.length || selectedRowsIds.length,
                failureCount: 0,
            });
            setSelectedRowsIds([]);
            closeModal();
        }
        catch (err) {
            handleGraphQLError(err, showError, t);
            tracking.bulkActionPerformed({
                action: 'assign_group',
                selectedCount: selectedRowsIds.length,
                successCount: 0,
                failureCount: selectedRowsIds.length,
            });
            setSelectedRowsIds([]);
            closeModal();
        }
    };
    const onAddToOrgConfirm = async (orgId) => {
        hideError();
        const userIds = [...selectedRowsIds];
        try {
            await addUsersToOrg({
                userids: selectedRowsIds,
                orgid: Number(orgId),
            });
            onSuccess(MODE.ASSIGN_USERS_ORG);
            userIds.forEach(id => tracking.orgAssigned({
                targetUserId: id,
                orgCount: 1,
                assignmentSource: 'bulk',
            }));
            tracking.bulkActionPerformed({
                action: 'assign_org',
                selectedCount: userIds.length,
                successCount: userIds.length,
                failureCount: 0,
            });
            setSelectedRowsIds([]);
            closeModal();
        }
        catch (err) {
            handleGraphQLError(err, showError, t);
            tracking.bulkActionPerformed({
                action: 'assign_org',
                selectedCount: userIds.length,
                successCount: 0,
                failureCount: userIds.length,
            });
            setSelectedRowsIds([]);
            closeModal();
        }
    };
    const bulkUpdateUserState = async (state) => {
        setIsLoading(true);
        const targets = rowData
            .filter(u => selectedRowsIds.includes(u.id))
            .map(u => ({ id: u.id, previousStatus: u.userState }));
        const ids = targets.map(t => t.id);
        const action = state === UserStateTransition.UNSUSPEND ? 'activate' : 'deactivate';
        try {
            await Promise.all(ids.map(id => updateUser({
                userid: id,
                content: {},
                password: '',
                userAccountState: state,
                isAllOrgContext: isClusterAdmin,
                triggeredbyadmin: true,
            })));
            onSuccess(MODE.UPDATE);
            targets.forEach(target => tracking.statusChanged({
                targetUserId: target.id,
                previousStatus: target.previousStatus,
                newStatus: state,
            }));
            tracking.bulkActionPerformed({
                action,
                selectedCount: ids.length,
                successCount: ids.length,
                failureCount: 0,
            });
        }
        catch (err) {
            handleGraphQLError(err, showError, t);
            tracking.bulkActionPerformed({
                action,
                selectedCount: ids.length,
                successCount: 0,
                failureCount: ids.length,
            });
        }
        finally {
            setIsLoading(false);
        }
    };
    const bulkResendActivation = async () => {
        setIsLoading(true);
        const ids = [...selectedRowsIds];
        const freeTrial = isFreeTrialOrTeamOrDeveloperUser();
        try {
            await Promise.all(ids.map(id => freeTrial
                ? reInviteMember(id)
                : updateUser({
                    userid: id,
                    content: {},
                    password: '',
                    userAccountState: UserStateTransition.REACTIVATE,
                    isAllOrgContext: isClusterAdmin,
                    triggeredbyadmin: true,
                })));
            onSuccess(MODE.RESEND_ACTIVATION);
            ids.forEach(id => tracking.userInvited({
                userID: id,
                isReinvite: true,
                isBulk: true,
                userCount: ids.length,
            }));
        }
        catch {
            showError(t(V2TranslationKeys.ACTIVATION_MAIL_ERROR));
        }
        finally {
            setIsLoading(false);
        }
    };
    const onRowActionRef = useRef(() => { });
    onRowActionRef.current = ({ action, data }) => {
        setCurrentUser(data);
        switch (action) {
            case ListActionTypeV2.VIEW_DETAILS:
                tracking.detailsViewed({
                    targetUserId: data.id,
                    tab: 'profile',
                    userAccountAge: data.created
                        ? Math.max(0, Math.floor((Date.now() - data.created) /
                            (1000 * 60 * 60 * 24)))
                        : 0,
                });
                setActiveModal(ActiveModal.USER_DETAILS);
                break;
            case ListActionTypeV2.EDIT:
                tracking.editClicked({
                    targetUserId: data.id,
                    fieldScope: 'profile',
                });
                setActiveModal(ActiveModal.EDIT_USER);
                break;
            case ListActionTypeV2.DELETE:
                tracking.deleteClicked({
                    targetUserId: data.id,
                    userAccountAge: data.created
                        ? Math.max(0, Math.floor((Date.now() - data.created) /
                            (1000 * 60 * 60 * 24)))
                        : 0,
                    orgMembershipCount: data.orgIds?.length ?? 0,
                });
                setActiveModal(ActiveModal.DELETE);
                break;
            case ListActionTypeV2.EXPORT:
                exportUsers([data.id]);
                break;
            case ListActionTypeV2.RESEND_ACTIVATION_MAIL:
                if (isFreeTrialOrTeamOrDeveloperUser()) {
                    setIsLoading(true);
                    pendingReinviteIdRef.current = data.id;
                    reInviteMember(data.id);
                }
                else {
                    updateUserState(data.id, UserStateTransition.REACTIVATE, data.userState);
                }
                break;
            case ListActionTypeV2.UNLOCK:
                updateUserState(data.id, UserStateTransition.UNLOCK, data.userState);
                break;
            case ListActionTypeV2.UNSUSPEND:
                updateUserState(data.id, UserStateTransition.UNSUSPEND, data.userState);
                break;
            case ListActionTypeV2.RESET_PASSWORD:
                setGeneratedOtp('');
                setOtpErrorMessage('');
                setShowOtpLoader(false);
                setActiveModal(ActiveModal.RESET_PASSWORD);
                break;
            case ListActionTypeV2.RESET_MFA:
                setIsLoading(true);
                resetMfaConfig({
                    clientId: getClientId(),
                    defaultHeaders: getDefaultSamlHeaders(),
                    userId: data.name,
                });
                break;
            default:
                break;
        }
    };
    const onGenerateOtp = async () => {
        if (!currentUser)
            return;
        setShowOtpLoader(true);
        setOtpErrorMessage('');
        try {
            const response = await generateOtp(currentUser.id);
            if (response?.data?.Session__generateOtp) {
                setGeneratedOtp(response.data.Session__generateOtp);
            }
            else {
                setOtpErrorMessage(V2TranslationKeys.OTP_GENERATION_FAILED);
            }
        }
        catch {
            setOtpErrorMessage(V2TranslationKeys.OTP_GENERATION_FAILED);
        }
        finally {
            setShowOtpLoader(false);
        }
    };
    const onRowActionCB = useCallback((payload) => onRowActionRef.current(payload), []);
    const bulkHandlers = {
        onDelete: () => setActiveModal(ActiveModal.DELETE),
        onExport: () => {
            if (isExportObjectLimitReached(selectedRowsIds)) {
                showError(t(V2TranslationKeys.EXPORT_LIMIT_ERROR, {
                    limit: getPrincipalObjectExportLimit(),
                }));
            }
            else {
                setActiveModal(ActiveModal.EXPORT);
            }
        },
        onAddToGroups: () => setActiveModal(ActiveModal.ADD_TO_GROUPS),
        onAddToOrg: () => setActiveModal(ActiveModal.ADD_TO_ORG),
        onUnlockAll: () => bulkUpdateUserState(UserStateTransition.UNLOCK),
        onUnsuspendAll: () => bulkUpdateUserState(UserStateTransition.UNSUSPEND),
        onResendActivationAll: bulkResendActivation,
    };
    const selectedUsers = useMemo(() => rowData.filter(u => selectedRowsIds.includes(u.id)), [rowData, selectedRowsIds]);
    const bulkActions = selectedRowsIds.length > 0
        ? getBulkActions(selectedUsers, bulkHandlers, isClusterAdmin)
        : [];
    const listViewProps = {
        data: rowData,
        columns: getColumnDefinitionsV2(isClusterAdmin),
        options: {
            suppressMovable: true,
            hasCheckboxes: true,
            onRowsSelected,
            selectedRowsIds,
            checkboxHeaderClass: [styles.headerStyle],
            customWidth: 48,
        },
        agGridContext: {
            onRowAction: onRowActionCB,
            orgNameMap,
            isClusterAdmin,
        },
        onSortingChanged,
        loading: gridLoading,
        setAgGridApi: onGridReady,
        suppressKeyboardEvent: (params) => params.colDef.colId === TableColumnId.ACTIONS,
    };
    const onCreateUserSuccess = (welcomeEmailFailed) => {
        hideError();
        refreshUsersList();
        if (welcomeEmailFailed) {
            setToast({
                message: t('users.wizard.toast.addSuccessWelcomeEmailFailed'),
            });
        }
        else {
            setToast({
                message: t('users.wizard.toast.addSuccess'),
                primaryAction: {
                    label: t('users.wizard.toast.addUser'),
                    onClick: () => {
                        clearToast();
                        setActiveModal(ActiveModal.CREATE_USER);
                    },
                },
            });
        }
        closeModal();
    };
    const onEditUserSuccess = () => {
        hideError();
        refreshUsersList();
        setToast({ message: t(V2TranslationKeys.EDIT_SUCCESS) });
        closeModal();
    };
    const onImportUsers = () => redirectToImport({
        hashRoute: '#/import-tsl/USER',
        allOrgsOverride: isClusterAdmin,
    });
    return (React.createElement(React.Fragment, null,
        React.createElement(UsersList, { rowData: rowData, selectedRowsIds: selectedRowsIds, isLoading: isLoading, bulkActions: bulkActions, metadataList: metadataList, listViewProps: listViewProps, pagination: {
                offset: userListInput.record_offset ?? 0,
                batchSize: userListInput.record_size ?? DEFAULT_BATCH_SIZE,
                isLastBatch,
                onChangeOffset,
            }, splitButton: {
                isTmlEnabled,
            }, feedback: {
                errorMessage,
                toastMessage: toast.message,
                toastPrimaryAction: toast.primaryAction,
                hideError,
                setToastMessage: clearToast,
            }, filterState: {
                groupIds: toStringArray(userListInput.group_identifiers).filter((id) => !!id),
                orgIds: toStringArray(userListInput.org_identifiers).filter((id) => !!id),
                orgOptions: orgFilterOptions,
                orgsLoading: orgsListLoading,
                accountStatus: userListInput.account_status ?? null,
                onGroupsChange: onGroupFilterChange,
                onOrgsChange: onOrgFilterChange,
                onStatusChange: onStatusFilterChange,
                onClearAll: onClearAllFilters,
                isClusterAdmin,
                isFilterDisabled: rowData.length === 0 &&
                    !userListInput.group_identifiers &&
                    !userListInput.org_identifiers &&
                    !userListInput.account_status,
            }, searchValue: userListInput.name_pattern ?? '', onSearchChange: onSearchChange, setActiveModal: setActiveModal, onImportUsers: onImportUsers }),
        React.createElement(UsersModals, { activeModal: activeModal, currentUser: currentUser, selectedRowsIds: selectedRowsIds, isClusterAdmin: isClusterAdmin, currentOrgId: currentOrgId, deleteUserLoading: deleteUserLoading, closeModal: closeModal, setActiveModal: setActiveModal, orgNameMap: orgNameMap, settingsInitialTab: settingsInitialTab, exportProps: {
                isGetEdocLoading,
                shouldExportDependencies,
                onRadioChange: onExportRadioChange,
                exportUsers,
            }, callbacks: {
                onDeleteConfirm,
                onAddToGroupsConfirm,
                onAddToOrgConfirm,
                onResetPasswordConfirm: closeModal,
                onGenerateOtp,
                generatedOtp,
                otpErrorMessage,
                showOtpLoader,
            }, onCreateUserSuccess: onCreateUserSuccess, onEditUserSuccess: onEditUserSuccess })));
};
export default UsersContainer;
//# sourceMappingURL=users.container.js.map